import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorParser } from '@app/classes';
import { ConfirmationDialogComponent } from '@app/components';
import { SocketEventTypes } from '@app/enums';
import { NotifyService, SocketService } from '@app/services';
import { Payroll } from '@models/payroll/payroll.model';
import { TranslateService } from '@ngx-translate/core';
import { PayrollStates } from '@payroll/enums/payroll-state.enum';
import { PayrollPayStatus } from '@payroll/interfaces/payroll-pay-status.interface';
import { PayrollService } from '@payroll/services';
import { Subscription } from 'rxjs';

@Component({
    templateUrl: './payroll-paying.template.html',
    styleUrls: ['./payroll-paying.style.scss'],
})
export class PayrollPayingView implements OnInit, OnDestroy {
    @ViewChild('payMultiEPTModal') payMultiEPTModal!: ConfirmationDialogComponent;

    isLoading = true;
    payroll?: Payroll;

    private payrollId: number;
    private completedPaystubs = 0;
    private totalPaystubs = 0;
    private subscriptions: Subscription[] = [];

    constructor(
        private notify: NotifyService,
        private route: ActivatedRoute,
        private router: Router,
        private payrollService: PayrollService,
        private socket: SocketService,
        private translateService: TranslateService
    ) {
        this.payrollId = this.route.snapshot.params.payroll;
    }

    /**
     * Subscribe to events and start the async pay job when component is initialized
     */
    ngOnInit(): void {
        this.subscribeToPayrollPayEvents();
        this.loadAndPayPayroll();
    }

    /**
     * Unsubscribe from all observables when component is destroyed
     */
    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }

    /**
     * Open the Humi support page in a new tab
     */
    openHelp(): void {
        window.open('https://support.humi.ca/', '_blank');
    }

    /**
     * Get the current progress percentage
     */
    getProgress(): number {
        if (this.totalPaystubs === 0) {
            return 0;
        }

        return Math.round((this.completedPaystubs / this.totalPaystubs) * 100);
    }

    get payrollType(): string {
        if (this.payroll === null) {
            return '';
        }
        const type = this.payroll?.recurring ? 'payroll.records.recurring' : 'payroll.records.offCycle';
        return this.translateService.instant(type);
    }

    /**
     * Set up subscriptions to payroll pay events
     */
    private subscribeToPayrollPayEvents(): void {
        this.subscriptions = [
            this.socket
                .getCompanyChannel(SocketEventTypes.payrollPayProgress)
                .subscribe((progress) => this.updateProgress(progress)),

            this.socket.getCompanyChannel(SocketEventTypes.payrollPayCompleted).subscribe(() => this.finishPay()),

            this.socket
                .getCompanyChannel(SocketEventTypes.payrollPayError)
                .subscribe((error) => this.handleError({ errors: [error] })),
        ];
    }

    /**
     * Load the payroll object and start the async pay job
     */
    private loadAndPayPayroll(): void {
        const payroll = this.route.snapshot.data.models.get('payroll');

        if (!payroll) {
            this.router.navigate(['/payroll', 'run'], { replaceUrl: true });

            return;
        }

        this.payroll = payroll;

        switch (payroll.state) {
            case PayrollStates.Paid:
                // Payroll's already paid, go straight to complete page
                this.goToComplete();
                break;
            case PayrollStates.Pending:
                // Kick off the payroll async pay job
                this.payrollService.asyncPay(payroll).catch((err) => this.handleError(err));
            case PayrollStates.Paying:
                // Status is pending or paying, sit tight and wait for events to come in
                break;
            default:
                // Any other state, return to run page
                this.router.navigate(['/payroll', 'run', payroll.id], { replaceUrl: true });
        }
    }

    /**
     * Update the progress bar
     */
    private updateProgress(progress: PayrollPayStatus): void {
        if (progress.completed === progress.total) {
            // Progress bar is at 100%, turn the spinner on until we receive the complete
            // event so the user sees that things are still happening (note this
            // may not occur every time since events are throttled)
            this.isLoading = true;
        } else {
            this.isLoading = false;
        }

        this.completedPaystubs = progress.completed;
        this.totalPaystubs = progress.total;
    }

    /**
     * Handle the complete event
     */
    private async finishPay(): Promise<void> {
        // Turn off the spinner as we're about to redirect
        this.isLoading = false;

        if (this.shouldShowEPTPayModal()) {
            await this.payMultiEPTModal.open();
        }

        this.goToComplete();
    }

    /**
     * Go to payroll summary
     */
    private goToComplete(): void {
        this.router.navigate(['/payroll', 'records', 'payrolls', this.payrollId, 'completed']);
    }

    /**
     * Show an error to the user
     */
    private handleError(error: Parameters<typeof ErrorParser.parse>[0]): void {
        this.notify.error(ErrorParser.parse(error));

        this.router.navigate(['/payroll', 'run', this.payrollId]);
    }

    /**
     * Whether the EPT Summary modal needs to be shown
     */
    private shouldShowEPTPayModal(): this is { payroll: Payroll } {
        return Number(this.payroll?.eptSummariesNonQc.length) > 0;
    }
}
